import React, { FC } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useQuery } from '@apollo/client';
import { volumeByPrimaryCptQuery } from '../../../../graph/dashboard';
import { useTheme } from '@material-ui/core';
import { renderActiveShape } from './charts/ORPerformancePieChart';
import ChartLegend from './charts/ChartLegend';
import { CustomTooltip } from './ProcedureCancellationChart';

const COLORS = ['#00A7F7', '#FFBB28', '#485796', '#2B3C87', '#44AABF', '#FFD166', '#FF6F61', '#3D9970', '#F38181'];

interface VolumeByPrimaryCptChartProps {
  filter: any;
}

/**
 * By-primary-CPT case-volume breakdown. Buckets (including the literal "Uncoded" bucket)
 * arrive pre-labeled from the backend — no client-side label resolution.
 * Mirrors ProcedureCancellationChart / totalCancellations.
 */
const VolumeByPrimaryCptChart: FC<VolumeByPrimaryCptChartProps> = ({ filter }) => {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = React.useState(0);
  const { data: fetchedData } = useQuery(volumeByPrimaryCptQuery, {
    variables: { filter },
    fetchPolicy: 'network-only',
  });

  const data = fetchedData?.volumeByPrimaryCpt?.data?.map(e => ({ name: e.name, value: e.value })) || [];

  const total = Math.round(data.reduce((acc, cur) => acc + cur.value, 0));

  return (
    <ResponsiveContainer width="100%" height="100%" aspect={1}>
      <PieChart>
        <Pie
          startAngle={90}
          endAngle={-270}
          dataKey="value"
          data={data}
          innerRadius="50%"
          outerRadius="70%"
          activeShape={props => renderActiveShape(props, `${total}`, 'Cases')}
          activeIndex={activeIndex}
          onMouseEnter={(_, index) => setActiveIndex(index)}
        >
          {data.map((entry, index) => (
            <Cell
              stroke="#1A2553"
              strokeWidth={3}
              style={{ outline: 'none' }}
              key={`cell-${index}`}
              fill={COLORS[index % COLORS.length]}
            />
          ))}
        </Pie>
        <Tooltip
          content={({ active, payload, label }) => <CustomTooltip active={active} payload={payload} label={label} />}
          cursor={{ fill: 'rgba(255,255,255,0.15)' }}
          contentStyle={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: 4,
            border: 0,
          }}
        />
        <Legend content={ChartLegend} align="left" verticalAlign="bottom" iconType="circle" iconSize={8} />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default VolumeByPrimaryCptChart;
